/**
 * Co se z googlovského nastavení smí ukázat — JEDINÉ místo, které to rozhoduje.
 * `@google-rozlozeni-klice`
 *
 * Známka i počet hodnocení byly do řezu 05 natvrdo zapsané na třech místech (4,9 / 596
 * ve veřejné i adminové mapě výchozích hodnot, `?? 4.9` a `?? 519` ve formuláři), takže
 * web tvrdil číslo, které nikdo nezměřil, a prázdné nastavení nešlo od vyplněného
 * odlišit. Po řezu nenese číslo ani jedna z těch tří map a o viditelnosti bloku
 * rozhodují VÝHRADNĚ dva pohledy tady — pro každý tvar plochy jeden:
 *  - `googleRatingView` (úvod, `/recenze`: karta + graf) vrací `null`, když nastavení
 *    nenese ani známku, ani úplné rozložení (vize F5.4, F5.5);
 *  - `googleRatingCompact` (detaily zájezdu, hotelu, inspirace, destinace: jen
 *    kompaktní karta) vrací `null` bez známky, protože karta bez ní nemá co ukázat
 *    a po sekci by zbyl prázdný pruh (AK4). Plocha pak jen testuje `null`.
 *
 * ── Proč jsou jména pěti klíčů JEN tady ──────────────────────────────────────
 *
 * Klíče potřebují staticky čtyři místa (veřejný typ nastavení, adminové rozhraní
 * `Settings`, obě mapy výchozích hodnot) a pole ve formuláři páté. Ručně vypsané by to
 * bylo pět výčtů téhož, které se rozejdou po jednom klíči. Všechna čtyři místa je proto
 * ODVOZUJÍ z `GOOGLE_RATING_KEYS` (typem `GoogleRatingCounts`, hodnotou
 * `googleRatingDefaults`) a že literály jmen nikde jinde nejsou, měří
 * `scripts/kontrola/ohlasy.mjs strom` — soubor, který by je potřeboval, se musí přiznat
 * značkou `@google-rozlozeni-klice` v docblocku.
 *
 * ── Nula je hodnota, ne prázdno ──────────────────────────────────────────────
 *
 * Provozovna opravdu nemusí mít ani jedno jednohvězdičkové hodnocení. „Vyplněné" se
 * proto testuje celočíselností a ne pravdivostí: `0 || null` by graf zahodil u každé
 * firmy, která nemá špatné recenze. Prázdné pole formuláře dorazí jako `null` (`""`
 * ani `NaN` vyplněné nejsou) — a `JSON.stringify(NaN)` je `"null"`, takže se prázdné
 * číselné pole přes nastavení proveze jako `null`, ne jako nula.
 */

/** Stupně škály v pořadí, v jakém se kreslí (odshora). */
export const GOOGLE_RATING_STARS = [5, 4, 3, 2, 1] as const;

/** Klíče nastavení nesoucí počet hodnocení daného stupně — JEDINÝ výčet jmen v repu. */
export const GOOGLE_RATING_KEYS = [
  "googleReviewStars5",
  "googleReviewStars4",
  "googleReviewStars3",
  "googleReviewStars2",
  "googleReviewStars1",
] as const;

export type GoogleRatingStars = (typeof GOOGLE_RATING_STARS)[number];
export type GoogleRatingKey = (typeof GOOGLE_RATING_KEYS)[number];

/** Pětice počtů tak, jak je nese nastavení. `null` = nevyplněno. */
export type GoogleRatingCounts = { [K in GoogleRatingKey]: number | null };

/** Výchozí hodnoty pro obě mapy nastavení — klíč zůstává, číslo v něm není. */
export const googleRatingDefaults: GoogleRatingCounts = Object.fromEntries(
  GOOGLE_RATING_KEYS.map((key) => [key, null]),
) as GoogleRatingCounts;

/** Klíč stupně — pořadí `GOOGLE_RATING_KEYS` odpovídá pořadí `GOOGLE_RATING_STARS`. */
export const googleRatingKeyOf = (stars: GoogleRatingStars): GoogleRatingKey =>
  GOOGLE_RATING_KEYS[GOOGLE_RATING_STARS.indexOf(stars)];

/**
 * Zdroj hodnot — část firemního nastavení, kterou vydává `/api/settings/public`
 * i adminové `GET /api/admin/settings/company`. Volné, protože obě odpovědi jdou přes
 * `JSON.parse` a web navíc degraduje na `{}`, když nastavení nedojede.
 */
export interface GoogleRatingSource extends Partial<GoogleRatingCounts> {
  googleReviewRating?: number | string | null;
  googleReviewCount?: number | string | null;
}

export interface GoogleRatingBar {
  stars: GoogleRatingStars;
  count: number;
  /** Podíl na celku (0–1) — z něj se počítá ŠÍŘKA pruhu, ať nesčítá zaokrouhlovací chyby. */
  share: number;
  /**
   * Celé procento — text vedle pruhu. Pět podílů se zaokrouhluje NEZÁVISLE, takže
   * součet smí utéct od stovky až o 2,5 bodu: 204/204/204/204/184 dá poctivých
   * 20+20+20+20+18 = 98. Mez měřidla je proto 97–103 (`TOLERANCE_PROCENT`
   * v `scripts/kontrola/ohlasy.mjs`), ne 99–101 — těsnější by červenala nad
   * správným výpočtem.
   */
  percent: number;
}

export interface GoogleRatingView {
  /** Známka; `null` = nastavení ji nenese. Nikdy se nedopočítává z rozložení. */
  score: number | null;
  /** Celkový počet hodnocení podle nastavení; `null` = nenese ho. */
  reviewCount: number | null;
  /** Součet pěti čísel rozložení; `null` = rozložení není úplné. */
  total: number | null;
  /** Pruhy grafu; `null` = graf se nekreslí (neúplné rozložení nebo součet 0). */
  bars: GoogleRatingBar[] | null;
}

/**
 * Celé číslo ≥ 0, nebo `null`. Řetězec se přijímá — nastavení jde přes `JSON.parse`.
 * Jediný parser počtu hodnocení (i ručního počtu v efektivním souhrnu Googlu).
 */
export function parseGoogleRatingCount(value: unknown): number | null {
  if (typeof value === "number") return Number.isInteger(value) && value >= 0 ? value : null;
  // `Number("")` je 0, takže prázdný řetězec se musí vyloučit DŘÍV než převodem.
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
  }
  return null;
}

/** Kladné konečné číslo (známka), nebo `null`. */
function scoreOf(value: unknown): number | null {
  const parsed = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** Pětice počtů, nebo `null`, když kterýkoli stupeň chybí. */
function fiveCounts(source: GoogleRatingSource | null | undefined): number[] | null {
  if (!source) return null;
  const values: number[] = [];
  for (const key of GOOGLE_RATING_KEYS) {
    const count = parseGoogleRatingCount((source as Record<string, unknown>)[key]);
    if (count === null) return null;
    values.push(count);
  }
  return values;
}

/** Součet pěti čísel rozložení; `null`, když některé chybí. */
export function googleRatingTotal(source: GoogleRatingSource | null | undefined): number | null {
  const values = fiveCounts(source);
  return values === null ? null : values.reduce((sum, value) => sum + value, 0);
}

/**
 * Celá procenta podílu `count / total`, zaokrouhlená DOLŮ. Jediné pravidlo procent
 * hodnocení na Googlu: dosazuje ho `{podíl}` věty o původu i řádek rozložení, takže
 * pravý sloupec `/recenze` neukáže pro pětihvězdičkové dvě různá čísla (867/894 = 96,98
 * → „96 %" ve větě i v rozložení). Dolů proto, že věta tvrdí „má {podíl} %" a nahoru
 * zaokrouhlené číslo by tvrdilo víc, než data dokládají. Celočíselně: `share * 100` by
 * 29/100 dalo 28. `total` musí být kladný (volající hlídá dělení nulou).
 */
export function googleSharePercent(count: number, total: number): number {
  return Math.floor((count * 100) / total);
}

/**
 * Pruhy grafu, nebo `null`.
 *
 * `null` i tehdy, když je všech pět vyplněných, ale součet je 0: procenta by se dělila
 * nulou a graf by tvrdil nedoložitelné rozložení nad nulou hodnocení.
 */
export function googleRatingBars(
  source: GoogleRatingSource | null | undefined,
): GoogleRatingBar[] | null {
  const values = fiveCounts(source);
  if (values === null) return null;
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total === 0) return null;
  return GOOGLE_RATING_STARS.map((stars, i) => {
    const count = values[i];
    const share = count / total;
    return { stars, count, share, percent: googleSharePercent(count, total) };
  });
}

/**
 * Pohled pro blok Google hodnocení; `null` = blok se nevykreslí.
 *
 * Brána je „známka NEBO úplné rozložení", ne jen známka: F5.5 mluví o PRÁZDNÉM
 * nastavení, tedy o tom, že nenese ani jedno. Nic se přitom nedopočítává — známka ze
 * součtu rozložení nevzniká a počet hodnocení se z něj taky neodvozuje (severka:
 * žádná dopočítaná hodnocení).
 */
export function googleRatingView(
  source: GoogleRatingSource | null | undefined,
): GoogleRatingView | null {
  const score = scoreOf(source?.googleReviewRating);
  const bars = googleRatingBars(source);
  if (score === null && bars === null) return null;
  return {
    score,
    reviewCount: parseGoogleRatingCount(source?.googleReviewCount),
    total: googleRatingTotal(source),
    bars,
  };
}

/** Kompaktní karta Google hodnocení — známka je povinná, počet volitelný. */
export interface GoogleRatingCompact {
  score: number;
  reviewCount: number | null;
}

/**
 * Pohled pro KOMPAKTNÍ kartu (detailní plochy); `null` = sekce se nevykreslí.
 *
 * Brána je jen známka: karta nekreslí graf, takže rozložení bez známky by po ní
 * nechalo prázdný obal sekce. Rozložení bez známky má svoji plochu na stránce ohlasů
 * (`googleRatingView`). Nic se nedopočítává, stejně jako tam.
 */
export function googleRatingCompact(
  source: GoogleRatingSource | null | undefined,
): GoogleRatingCompact | null {
  const score = scoreOf(source?.googleReviewRating);
  if (score === null) return null;
  return { score, reviewCount: parseGoogleRatingCount(source?.googleReviewCount) };
}
